/**
 * cropAiApi.js — API client for the Agri-AI backend.
 *
 * Base URL: same origin as the main backend, under /api/
 * All endpoints are documented in docs/CropAI/api-handoff.html
 *
 * IMPORTANT: Agriculture ID ↔ Farm ID Mapping
 * - The backend stores agriculture entities with agriculture_id (primary key)
 * - AI API endpoints expect "farm_id" parameter (which is the agriculture_id)
 * - The mapping is transparent: pass agriculture_id as farm_id to all AI endpoints
 *
 * No authentication required — the backend handles OpenAI keys internally.
 */

import { getAuthHeaders } from "./apiClient";

/** Resolve base for AI routes — AI backend is mounted under `/ai/api` on the server */
const aiBase = () => "/ai/api";

// ─── Farms ──────────────────────────────────────────────────────────────────

/**
 * GET /api/farms
 * Returns { farms: Farm[] }
 */
export async function fetchAiFarms() {
  const res = await fetch(`${aiBase()}/farms`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`farms: ${res.status}`);
  return res.json(); // { farms: [...] }
}

// ─── Chat ────────────────────────────────────────────────────────────────────

/**
 * POST /api/chat
 * Body: { messages, farm_id?, session_id? }
 * Returns { message: string }
 */
export async function sendChatMessage({ messages, farmId, sessionId }) {
  const body = { messages };
  if (farmId) body.farm_id = farmId;
  if (sessionId) body.session_id = sessionId;

  const res = await fetch(`${aiBase()}/chat`, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `chat: ${res.status}`);
  }
  return res.json(); // { message: string }
}

// ─── Map actions ─────────────────────────────────────────────────────────────

/**
 * POST /api/map
 * Body: { query: string }
 * Returns { action, farm_id, layer, message }
 */
export async function sendMapCommand(query) {
  const res = await fetch(`${aiBase()}/map`, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `map: ${res.status}`);
  }
  return res.json();
}

// ─── Reports ─────────────────────────────────────────────────────────────────

/**
 * POST /api/reports
 * Body: { farm_id: string }
 * Returns { report: string, farm_name: string, farm_id: string }
 * NOTE: this is slow (LLM call) — show a loading state.
 */
export async function generateReport(farmId) {
  const res = await fetch(`${aiBase()}/reports`, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ farm_id: farmId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `reports: ${res.status}`);
  }
  return res.json();
}

// ─── Sessions ────────────────────────────────────────────────────────────────

/** GET /api/sessions → { sessions: Session[] } */
export async function fetchSessions() {
  const res = await fetch(`${aiBase()}/sessions`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`sessions: ${res.status}`);
  return res.json();
}

/** POST /api/sessions → Session */
export async function createSession({ farmId, title } = {}) {
  const body = {};
  if (farmId) body.farm_id = farmId;
  if (title) body.title = title;
  const res = await fetch(`${aiBase()}/sessions`, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`create session: ${res.status}`);
  return res.json();
}

/** GET /api/sessions/{id}/messages → { messages: Message[] } */
export async function fetchSessionMessages(sessionId) {
  const res = await fetch(`${aiBase()}/sessions/${sessionId}/messages`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`session messages: ${res.status}`);
  return res.json();
}

/** DELETE /api/sessions/{id} → { ok: true } */
export async function deleteSession(sessionId) {
  const res = await fetch(`${aiBase()}/sessions/${sessionId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`delete session: ${res.status}`);
  return res.json();
}
